import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Plus, Eye, Check, X, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { invoicesAPI } from '../../services/api';
import InvoiceForm from '../InvoiceForm';
import EditInvoiceDialog from '../EditInvoiceDialog';
import InvoiceViewDialog from '../InvoiceViewDialog';
import PaymentModeDialog from '../PaymentModeDialog';
import DeleteConfirmDialog from '../DeleteConfirmDialog';

const getInvoiceStatusColor = (status) => {
  const colors = {
    draft: 'bg-gray-100 text-gray-800',
    sent: 'bg-blue-100 text-blue-800',
    pending: 'bg-yellow-100 text-yellow-800',
    partial: 'bg-orange-100 text-orange-800',
    paid: 'bg-green-100 text-green-800',
    overdue: 'bg-red-100 text-red-800'
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

const ClientInvoicesTab = ({ 
  clientId, 
  client,
  invoices, 
  services,
  canDeleteInvoice,
  onRefresh,
  toast 
}) => {
  const [invoiceDialog, setInvoiceDialog] = useState(false);
  const [editInvoiceDialog, setEditInvoiceDialog] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [viewInvoiceDialog, setViewInvoiceDialog] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [invoiceToPay, setInvoiceToPay] = useState(null);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState('cash');
  const [selectedPaidDate, setSelectedPaidDate] = useState('');
  const [selectedPaymentMemo, setSelectedPaymentMemo] = useState('');
  const [selectedPaymentAmount, setSelectedPaymentAmount] = useState('');
  const [deleteInvoiceDialog, setDeleteInvoiceDialog] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);

  const handleTogglePaid = async (invoice) => {
    if (invoice.status === 'paid') {
      // Unpay
      try {
        await invoicesAPI.unpay(invoice._id);
        toast({ title: 'Success', description: 'Invoice reset to unpaid' });
        onRefresh();
      } catch (error) {
        toast({ title: 'Error', description: 'Failed to update invoice', variant: 'destructive' });
      }
    } else {
      // Open payment dialog
      setInvoiceToPay(invoice);
      setSelectedPaymentMode('');
      setSelectedPaidDate(new Date().toISOString().split('T')[0]);
      setSelectedPaymentMemo('');
      // Default to remaining balance
      const remaining = (invoice.grandTotal || 0) - (invoice.paidAmount || 0);
      setSelectedPaymentAmount(remaining);
      setPaymentDialog(true);
    }
  };

  const handleConfirmPayment = async () => {
    if (!selectedPaymentMode) {
      toast({ title: 'Error', description: 'Please select a payment mode', variant: 'destructive' });
      return;
    }
    try {
      await invoicesAPI.pay(invoiceToPay._id, selectedPaymentMode, selectedPaidDate, selectedPaymentMemo, selectedPaymentAmount);
      toast({ title: 'Success', description: `Payment of $${selectedPaymentAmount?.toFixed(2) || '0.00'} recorded` });
      setPaymentDialog(false);
      setInvoiceToPay(null);
      setSelectedPaymentMode('');
      setSelectedPaidDate('');
      onRefresh();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to mark invoice as paid', variant: 'destructive' });
    }
  };

  const handleDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    try {
      await invoicesAPI.delete(invoiceToDelete._id);
      toast({ title: 'Success', description: 'Invoice deleted successfully' });
      setDeleteInvoiceDialog(false);
      setInvoiceToDelete(null);
      onRefresh();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast({ title: 'Error', description: 'Failed to delete invoice', variant: 'destructive' });
    }
  };

  const openEditInvoiceDialog = (inv) => {
    setEditingInvoice(inv);
    setEditInvoiceDialog(true);
  };

  const openDeleteInvoiceDialog = (inv) => {
    setInvoiceToDelete(inv);
    setDeleteInvoiceDialog(true);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Invoices</CardTitle>
            <Button size="sm" className="gap-2" onClick={() => setInvoiceDialog(true)} data-testid="add-invoice-btn">
              <Plus className="h-4 w-4" />
              Create Invoice
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {invoices.length > 0 ? (
            <div className="space-y-3">
              {invoices.map((inv) => (
                <div key={inv._id} className="p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-gray-900">{inv.invoiceNumber}</h3>
                        <Badge className={getInvoiceStatusColor(inv.status)}>{inv.status}</Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>Amount: <strong className="text-gray-900">${(inv.grandTotal || inv.amount || 0).toFixed(2)}</strong></span>
                        <span>Due: {inv.dueDate ? format(new Date(inv.dueDate), 'MMM dd, yyyy') : 'N/A'}</span>
                        {inv.paidDate && <span className="text-green-600">Paid: {format(new Date(inv.paidDate), 'MMM dd, yyyy')}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTogglePaid(inv)}
                        className="gap-1"
                      >
                        {inv.status === 'paid' ? (
                          <>
                            <X className="h-4 w-4" />
                            Unpay
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4" />
                            Mark Paid
                          </>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setViewingInvoice(inv); setViewInvoiceDialog(true); }}
                        className="gap-1"
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditInvoiceDialog(inv)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit Invoice
                          </DropdownMenuItem>
                          {canDeleteInvoice && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => openDeleteInvoiceDialog(inv)}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Invoice
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No invoices yet</p>
          )}
        </CardContent>
      </Card>

      {/* Create Invoice Dialog - Using InvoiceForm Component */}
      <InvoiceForm
        open={invoiceDialog}
        onOpenChange={setInvoiceDialog}
        preselectedClientId={clientId}
        preselectedClientName={client?.name}
        onSuccess={onRefresh}
      />

      {/* Edit Invoice Dialog */}
      <EditInvoiceDialog
        open={editInvoiceDialog}
        onOpenChange={(open) => {
          setEditInvoiceDialog(open);
          if (!open) setEditingInvoice(null);
        }}
        invoice={editingInvoice}
        onSuccess={onRefresh}
      />

      {/* View Invoice Dialog */}
      <InvoiceViewDialog
        open={viewInvoiceDialog}
        onOpenChange={setViewInvoiceDialog}
        invoice={viewingInvoice}
        client={client}
      />

      {/* Payment Mode Dialog */}
      <PaymentModeDialog
        open={paymentDialog}
        onOpenChange={setPaymentDialog}
        invoice={invoiceToPay}
        paymentMode={selectedPaymentMode}
        onPaymentModeChange={setSelectedPaymentMode}
        paidDate={selectedPaidDate}
        onPaidDateChange={setSelectedPaidDate}
        paymentMemo={selectedPaymentMemo}
        onPaymentMemoChange={setSelectedPaymentMemo}
        paymentAmount={selectedPaymentAmount}
        onPaymentAmountChange={setSelectedPaymentAmount}
        onConfirm={handleConfirmPayment}
      />

      {/* Delete Invoice Confirmation */}
      <DeleteConfirmDialog
        open={deleteInvoiceDialog}
        onOpenChange={setDeleteInvoiceDialog}
        title="Delete Invoice"
        description={`Are you sure you want to delete invoice ${invoiceToDelete?.invoiceNumber}? This action cannot be undone.`}
        onConfirm={handleDeleteInvoice}
      />
    </>
  );
};

export default ClientInvoicesTab;
